import { FC } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import DatePickerCustomHeaderOneMonth from "../../DatePickerCustomHeaderOneMonth";
import DatePickerCustomHeaderTwoMonth from "../../DatePickerCustomHeaderTwoMonth";

interface DatePickerModalProps {
  showDatePicker: "start" | "end" | null;
  tempSelectedDate: Date | null;
  handleModalDateChange: (date: Date | null) => void;
  handleModalSave: () => void;
  handleModalClose: () => void;
  monthsToShow:  number;
  calendarViewDate: Date | null;
  startDate: Date | null;
  isDateSelectable: (date:  Date) => boolean;
  renderDayContents: (day: number, date?:  Date) => React.ReactNode;
  handleMonthChange: (date: Date) => void;
  isLoading?: boolean;
}

const DatePickerModal:  FC<DatePickerModalProps> = ({
  showDatePicker,
  tempSelectedDate,
  handleModalDateChange,
  handleModalSave,
  handleModalClose,
  monthsToShow,
  calendarViewDate,
  startDate,
  isDateSelectable,
  renderDayContents,
  handleMonthChange,
  isLoading = false,
}) => {
  if (!showDatePicker) return null;

  const isStart = showDatePicker === "start";
  const title = isStart ? "Choose a start date" : "Choose an end date";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/10 backdrop-blur-sm rounded-xl"
        onClick={isLoading ? undefined : handleModalClose}
      />

      {/* Modal */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <button
              onClick={isLoading ? undefined : handleModalClose}
              disabled={isLoading}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XMarkIcon className="w-5 h-5 text-gray-600" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <div className="w-9" /> {/* Spacer for centering */}
          </div>
        </div>

        {/* Calendar */}
        <div className="p-6 relative">
          {isLoading && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
              <div className="flex flex-col items-center space-y-3">
                <div className="w-10 h-10 border-4 border-primary-200 border-t-transparent rounded-full animate-spin"></div>
               
              </div>
            </div>
          )}
          
          <div className="flex justify-center">
            <DatePicker
              selected={tempSelectedDate}
              onChange={handleModalDateChange}
              monthsShown={monthsToShow}
              showPopperArrow={false}
              inline
              minDate={new Date()}
              openToDate={calendarViewDate || startDate || new Date()}
              filterDate={isDateSelectable}
              renderCustomHeader={(p) => (
                <>
                  <div className="block sm:hidden">
                    <DatePickerCustomHeaderOneMonth {... p} />
                  </div>
                  <div className="hidden sm:block">
                    <DatePickerCustomHeaderTwoMonth {...p} />
                  </div>
                </>
              )}
              renderDayContents={renderDayContents}
              onMonthChange={handleMonthChange}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4">
          <div className="flex items-center justify-end">
            <button
              onClick={handleModalSave}
              disabled={! tempSelectedDate}
              className="px-8 py-3 bg-primary-200 hover:bg-primary-700 text-button-color font-semibold rounded-lg transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatePickerModal;